import {isFeatureEnabled} from 'feature-flags';
import {Button, Card, DataStatus} from 'platform/components';
import {Hide, HStack, Show, Space} from 'platform/foundation';

import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {any, defaultTo, head, isNotNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {useDeleteInspectionMutation} from '@dms/api/core';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {queryParams, handleApiError} from '@dms/shared';
import {DigitalCertificateLink} from '@dms/shared-aftersales';
import {
  selectAudit,
  LoadAuditDataResponseItemBody,
  useThunkDispatch,
  getAudit,
  updateAuditData,
  Audit,
  selectCarAudit,
  getAuditStructure,
  ReopenInspection,
  FinishInspection,
} from '@dms/teas';

import {TestIdProps, suffixTestId, useQueryState, Nullish} from 'shared/web';

interface InspectionProps extends TestIdProps {
  vehicleId: string;
  inspectionId: string;
  isCompletionDisabled: boolean;
  isReadOnly?: boolean;

  /**
   * @deprecated Use onSendEmailSmsNotification instead
   */
  onSendEmailSmsNotification: () => void;

  onSendSmsNotification: () => void;
  onSendEmailNotification: () => void;
  onInspectionDeleted: () => void;
}

export function Inspection(props: InspectionProps) {
  const [activeInspectionTab, setActiveInspectionTab] = useQueryState(
    queryParams.SERVICE_CASE_INSPECTION
  );

  const [auditFetched, setAuditFetched] = useState(false);
  const [isAuditError, setIsAuditError] = useState(false);
  const [deleteInspection, deleteInspectionQuery] = useDeleteInspectionMutation();

  const dispatch = useThunkDispatch();

  const audit = useSelector(selectAudit);
  const {loading} = useSelector(selectCarAudit);

  const isAuditLoading = any(isTrue, [
    loading.getAudit,
    loading.getAuditStructure,
    loading['vehicleCatalogue/getCustomTenantCatalogue'],
  ]);

  useEffect(
    function fetchInspectionAudit() {
      if (auditFetched || isAuditError) {
        return;
      }

      const fetchAuditStructure = (auditStructure: LoadAuditDataResponseItemBody | Nullish) => {
        if (isNotNil(auditStructure?.controlStructureId)) {
          dispatch(
            getAuditStructure.action({auditStructureId: auditStructure?.controlStructureId})
          );
          setAuditFetched(true);
        }
      };

      if (isNotNil(props.vehicleId) && isNotNil(props.inspectionId) && !isAuditLoading) {
        dispatch(
          getAudit.action({
            auditId: props.inspectionId,
          })
        )
          .unwrap()
          .then((response) => {
            if (isNotNil(response)) {
              fetchAuditStructure(head(response));
            }
          })
          .catch(() => setIsAuditError(true));
      }
    },
    [props.vehicleId, props.inspectionId, auditFetched, isAuditLoading, dispatch, isAuditError]
  );

  const handleInspectionTabChange = (id: string) => setActiveInspectionTab(id);

  const isAuditFinished =
    audit?.state === LoadAuditDataResponseItemBody.state.FINISHED_AUDIT && !isAuditError;

  useEffect(
    function removeAuditOnUnmount() {
      // Remove audit when component unmounts
      return () => {
        dispatch(updateAuditData({auditData: undefined}));
      };
    },
    [dispatch]
  );

  return (
    <Card title={i18n.t('page.Inspection.labels.title')}>
      <DataStatus isLoading={isAuditLoading} isError={isAuditError}>
        <Audit
          activeTab={defaultTo(undefined, activeInspectionTab)}
          onTabChange={handleInspectionTabChange}
          vehicleId={props.vehicleId}
          isInspection
          isBodyOnly
          isCompletionDisabled={props.isCompletionDisabled}
          customFooterContent={
            <Hide when={props.isReadOnly}>
              <HStack spacing={2}>
                <Show when={isAuditFinished}>
                  <Space fillAvailable />
                  <ReopenInspection
                    onSuccess={() => {
                      setAuditFetched(false);
                      setIsAuditError(false);
                    }}
                    inspectionId={props.inspectionId}
                  />
                </Show>
                <Show when={!isAuditFinished}>
                  <Button
                    title={i18n.t('general.actions.remove')}
                    leftIcon="action/delete"
                    variant="dangerGhost"
                    isLoading={deleteInspectionQuery.isLoading}
                    onClick={() =>
                      deleteInspection({inspectionId: props.inspectionId, force: true})
                        .unwrap()
                        .then(() => props.onInspectionDeleted?.())
                        .catch(handleApiError)
                    }
                    data-testid={suffixTestId('remove', props)}
                  />
                  <Space fillAvailable />
                  <FinishInspection
                    buttonTitle={i18n.t('general.actions.complete')}
                    showWhenFinished
                    isDisabled={props.isCompletionDisabled}
                  />
                </Show>
              </HStack>
            </Hide>
          }
          customHeaderContent={
            <HStack spacing={2}>
              <Hide when={props.isReadOnly}>
                <Hide when={isFeatureEnabled(featureFlags.MESSAGE_EMAIL_REDESIGN_AFTERSALES)}>
                  <Button
                    title={i18n.t('entity.order.actions.sendEmailSms')}
                    variant="outlined"
                    isDisabled={!isAuditFinished}
                    onClick={props.onSendEmailSmsNotification}
                    data-testid={suffixTestId('sendEmailSms', props)}
                  />
                </Hide>
                <Show when={isFeatureEnabled(featureFlags.MESSAGE_EMAIL_REDESIGN_AFTERSALES)}>
                  <Button
                    title={i18n.t('general.actions.sendEmail')}
                    variant="outlined"
                    isDisabled={!isAuditFinished}
                    onClick={props.onSendEmailNotification}
                    data-testid={suffixTestId('sendEmailSms', props)}
                  />
                </Show>

                <Button
                  title={i18n.t('general.actions.sendSms')}
                  variant="outlined"
                  isDisabled={!isAuditFinished}
                  onClick={props.onSendSmsNotification}
                  data-testid={suffixTestId('sendEmailSms', props)}
                />
              </Hide>

              <DigitalCertificateLink
                inspectionId={props.inspectionId}
                isDisabled={!isAuditFinished}
              />
            </HStack>
          }
          data-testid={props['data-testid']}
        />
      </DataStatus>
    </Card>
  );
}
